import { createElement, useMemo } from 'react';
import type { ReactNode } from 'react';

/**
 * Formatierter Text, ohne ihn als HTML einzusetzen -- F1-10.
 *
 * **Warum es diese Komponente gibt.** Beitraege, Wissensartikel und
 * Mailvorlagen werden als HTML gespeichert; `core/html.sanitise_html`
 * bereinigt sie beim Schreiben. Fuer die Anzeige gab es bisher zwei Wege, und
 * beide waren unbefriedigend:
 *
 * * Der Ticketverlauf zeigte den **Klartext**. Sicher, aber damit war jede
 *   Auszeichnung unsichtbar -- ein Editor, dessen Ergebnis niemand zu sehen
 *   bekommt, ist keiner.
 * * Die Wissensdatenbank setzte das HTML mit `dangerouslySetInnerHTML` ein.
 *   Lesbar, aber die eine Stelle im Frontend, an der eine Luecke im Reiniger
 *   unmittelbar zu ausgefuehrtem Code fuehrte.
 *
 * Hier wird ein dritter Weg gegangen: Das HTML wird **geparst** und daraus
 * werden React-Elemente gebaut. Was nicht auf der Liste steht, entsteht gar
 * nicht erst -- es gibt keinen Weg von einer Zeichenkette zu einem Element,
 * den der Browser interpretieren koennte. `dangerouslySetInnerHTML` kommt im
 * ganzen Modul nicht vor.
 *
 * **Die Liste ist dieselbe wie im Server.** `ALLOWED_TAGS` und
 * `ALLOWED_ATTRIBUTES` spiegeln `backend/src/itscrm/core/html.py`. Wer dort
 * etwas aufnimmt, nimmt es hier mit auf -- sonst verschwindet es beim
 * Anzeigen, ohne dass jemand den Zusammenhang faende. Ein Unterschied in
 * diese Richtung ist harmlos (es wird weniger gezeigt, nicht mehr); der
 * umgekehrte waere es nicht.
 *
 * **Die Bereinigung beim Schreiben bleibt.** Diese Komponente ersetzt sie
 * nicht, sie steht daneben: Der Server bereinigt einmal beim Speichern, das
 * Frontend prueft noch einmal beim Anzeigen. Der Altbestand aus der Zeit vor
 * einer Regelverschaerfung laeuft so durch die neue Regel, ohne dass jemand
 * die Datenbank anfassen muesste.
 */

/** Was angezeigt werden darf -- Spiegel von `ALLOWED_TAGS` im Server. */
const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'div',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'a',
  'img',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'hr',
]);

/**
 * Was **samt Inhalt** verschwindet.
 *
 * Der Unterschied zu einem unbekannten Element ist wesentlich. Ein `<font>`
 * aus einer alten Mail traegt Text, den der Leser sehen soll -- das Element
 * faellt weg, der Text bleibt. Bei einem `<script>` ist der Inhalt gerade das
 * Gefaehrliche: Als Text ausgegeben stuende der Quelltext eines Angriffs im
 * Beitrag. Beides waere nicht ausgefuehrt, aber das eine ist ein Beitrag und
 * das andere Muell.
 */
const DROP_ENTIRELY = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'template',
  'noscript',
  'svg',
  'math',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'link',
  'meta',
  'base',
  'title',
]);

/** Welches Element welche Merkmale behalten darf. */
const ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href', 'title']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan']),
  blockquote: new Set(['class']),
};

/** Schemata, die ein Verweis tragen darf. `javascript:` und `data:` fehlen. */
const ALLOWED_URL_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * Eingebettete Bilder zeigen ausschliesslich auf eigene Anhaenge.
 *
 * Ein Bild von einem fremden Server waere ein Zaehlpixel: Es meldet dem
 * Absender, wann und von wem ein Ticket gelesen wurde. Derselbe Praefix wie
 * `ALLOWED_IMAGE_PREFIX` im Server.
 */
const ALLOWED_IMAGE_PREFIX = '/api/v1/attachments/';

/**
 * Klassennamen, die eine Zitatkennzeichnung tragen darf.
 *
 * Der Server laesst `class` am `blockquote` durch. Freie Klassennamen aus
 * einer eingegangenen Mail duerfen aber nicht das Aussehen der Oberflaeche
 * treffen -- ein `class="card"` aus fremder Quelle saehe aus wie ein Bauteil
 * der Anwendung. Deshalb hier nur, was der Editor selbst setzt.
 */
const ALLOWED_BLOCKQUOTE_CLASSES = new Set(['quote']);

/**
 * Wie tief die Schachtelung verfolgt wird.
 *
 * Eine Reissleine und keine fachliche Grenze: Ein Dokument mit zweihundert
 * ineinander geschachtelten Listen kommt aus keinem Mailprogramm, sondern aus
 * einem Versuch, die Rekursion hier zum Absturz zu bringen.
 */
const MAX_DEPTH = 60;

/** Elemente ohne Inhalt -- React weist ihnen keine Kinder zu. */
const VOID_TAGS = new Set(['br', 'hr', 'img']);

function safeUrl(value: string, { image }: { image: boolean }): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  if (image) {
    // Nur der eigene Anhangspfad, und zwar von Anfang an. Ein `..` darin
    // fuehrte zwar nirgendwohin, aber der Vergleich soll ohne Nachdenken
    // lesbar bleiben.
    return trimmed.startsWith(ALLOWED_IMAGE_PREFIX) && !trimmed.includes('..') ? trimmed : null;
  }

  // Ein Verweis ohne Schema ist relativ und bleibt damit im eigenen Ursprung.
  // `//fremder.host` ist es **nicht** -- das ist schemenrelativ und fuehrt
  // hinaus.
  if (trimmed.startsWith('//')) return null;
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return trimmed;

  try {
    const url = new URL(trimmed, window.location.origin);
    return ALLOWED_URL_SCHEMES.has(url.protocol) ? trimmed : null;
  } catch {
    return null;
  }
}

function attributesFor(element: Element, tag: string): Record<string, unknown> {
  const allowed = ALLOWED_ATTRIBUTES[tag];
  const props: Record<string, unknown> = {};
  if (!allowed) return props;

  for (const name of allowed) {
    const raw = element.getAttribute(name);
    if (raw === null) continue;

    if (name === 'href') {
      const href = safeUrl(raw, { image: false });
      if (href === null) continue;
      props.href = href;
      // Ein Verweis aus einem Beitrag fuehrt aus der Anwendung heraus. Er
      // oeffnet deshalb ein eigenes Fenster -- und `noopener` sorgt dafuer,
      // dass die Zielseite keinen Griff auf unseres bekommt.
      props.target = '_blank';
      props.rel = 'noopener noreferrer nofollow';
      continue;
    }

    if (name === 'src') {
      const src = safeUrl(raw, { image: true });
      if (src === null) continue;
      props.src = src;
      // Ein Bild aus einem Beitrag laedt erst, wenn es in den Blick kommt.
      props.loading = 'lazy';
      continue;
    }

    if (name === 'class') {
      const kept = raw
        .split(/\s+/)
        .filter((value) => ALLOWED_BLOCKQUOTE_CLASSES.has(value))
        .join(' ');
      if (kept !== '') props.className = kept;
      continue;
    }

    if (name === 'colspan' || name === 'rowspan' || name === 'width' || name === 'height') {
      // Nur Ziffern. Ein `colspan="1e9"` liesse den Browser eine Tabelle mit
      // einer Milliarde Spalten aufbauen.
      if (!/^\d{1,4}$/.test(raw)) continue;
      const key = name === 'colspan' ? 'colSpan' : name === 'rowspan' ? 'rowSpan' : name;
      props[key] = Number(raw);
      continue;
    }

    props[name] = raw;
  }

  return props;
}

function render(nodes: NodeListOf<ChildNode>, depth: number): ReactNode[] {
  if (depth > MAX_DEPTH) return [];

  const out: ReactNode[] = [];

  nodes.forEach((node, index) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text !== '') out.push(text);
      return;
    }

    // Kommentare, Verarbeitungsanweisungen, Dokumenttypen: nichts davon hat
    // in einer Anzeige etwas zu suchen.
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node as Element;
    const tag = element.tagName.toLowerCase();

    if (DROP_ENTIRELY.has(tag)) return;

    const children = render(element.childNodes, depth + 1);

    if (!ALLOWED_TAGS.has(tag)) {
      // Unbekanntes Element: Der Rahmen faellt weg, der Inhalt bleibt.
      out.push(...children);
      return;
    }

    const props = attributesFor(element, tag);
    props.key = `${tag}-${String(index)}-${String(depth)}`;

    if (VOID_TAGS.has(tag)) {
      // `img` ohne gueltige Quelle waere ein leerer Rahmen mit dem
      // Ersatztext des Browsers. Es faellt ganz weg.
      if (tag === 'img' && props.src === undefined) return;
      out.push(createElement(tag, props));
      return;
    }

    out.push(createElement(tag, props, ...children));
  });

  return out;
}

interface RichTextProps {
  /**
   * Das bereits vom Server bereinigte HTML.
   *
   * Nullbar, obwohl das Schema es nicht ist: Ein Beitrag aus der Zeit vor
   * ``body_html`` traegt die Spalte nicht, und eine Anzeige, die daran
   * abbricht, waere die schlechtere Antwort als eine leere.
   */
  html: string | null | undefined;
  /**
   * Ersatz, wenn nichts da ist. Ohne ihn bleibt die Stelle leer -- ein
   * Beitrag ohne Text kommt vor, etwa eine Mail mit nur einem Anhang.
   */
  fallback?: ReactNode;
  className?: string;
}

export function RichText({
  html,
  fallback = null,
  className = 'prose prose--rich',
}: RichTextProps) {
  const content = useMemo(() => {
    if (!html?.trim()) return null;
    // `DOMParser` mit `text/html` erzeugt ein **traeges** Dokument: Skripte
    // laufen nicht, Bilder werden nicht geladen, `onerror` feuert nicht. Das
    // ist der Unterschied zu `innerHTML` an einem Element im Seitenbaum.
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nodes = render(parsed.body.childNodes, 0);
    return nodes.length > 0 ? nodes : null;
  }, [html]);

  if (content === null) return <>{fallback}</>;

  return <div className={className}>{content}</div>;
}
